import { useMemo } from 'react';

import { differenceInSeconds } from 'date-fns';

import * as crypto from 'crypto-js';

import { ReportConfig } from '@app-velocity/common';
import {
  TAiSimulationPartialDto,
  TAiSimulationsFullSummaryDto,
  TPrototypeDto,
  TPrototypeReviewDto,
  TReviewUiEventDto,
} from '@app-velocity/av-api-v2';

import { PrototypeStatistics } from '../../types';
import { useConfidence } from './hooks/useConfidence';
import { useReportConfig } from '../useReportConfig';

type PagesMap = {
  [pageId: string]: string;
};

const getViewChecksum = (
  pageId: string,
  partials: TAiSimulationPartialDto[],
  map: PagesMap
) => {
  const value = `${map[pageId]}${partials
    .map((partial) => partial.id)
    .join('-')}`;

  const hash = crypto.MD5(`${value}`);

  return hash.toString();
};

const areRequirementsMeet = (
  humanTestersCount: number,
  aiSimulatedTestersCount: number,
  missClickRate: number,
  dropOffRate: number,
  successRate: number,
  timeToSuccess: number,
  config: ReportConfig
): boolean => {
  if (humanTestersCount < config.minimumHumanParticipants) return false;
  if (aiSimulatedTestersCount < config.minimumAiParticipants) return false;
  if (missClickRate * 100 > config.missclickRate) return false;
  if (dropOffRate * 100 > config.dropOffRate) return false;
  if (successRate * 100 < config.successRate) return false;
  if (timeToSuccess > config.timeToSuccess) return false;

  return true;
};

export const usePrototypeStatistics = (
  scenario?: TPrototypeDto | null,
  simulationsSummary?: TAiSimulationsFullSummaryDto | null,
  humanReviews?: TPrototypeReviewDto[],
  humanEvents?: TReviewUiEventDto[]
): PrototypeStatistics | null => {
  const { config } = useReportConfig(
    scenario?.projectId || '',
    scenario?.id || ''
  );

  const pagesMap = useMemo(() => {
    if (!scenario) return {};
    const { prototypePages } = scenario;
    if (!prototypePages) return {};

    const map: PagesMap = {};

    for (let i = 0; i < prototypePages.length; i++) {
      const page = prototypePages[i];
      const { id } = page;
      map[id!] = `${i}`.padStart(3, '-');
    }

    return map;
  }, [scenario]);

  const allSimulations = simulationsSummary?.simulations || [];
  const correctSimulations = allSimulations.filter(
    (simulation) =>
      !['ABORTED_BY_USER', 'ABORTED_ERROR', 'ABORTED_TIMEOUT'].includes(
        simulation.status!
      )
  );

  const succeded = correctSimulations.filter((simulation) =>
    simulation.status?.includes('FINISHED_')
  );

  const failed = correctSimulations.filter(
    (simulation) =>
      !['ABORTED_BY_USER', 'ABORTED_ERROR', 'ABORTED_TIMEOUT'].includes(
        simulation.status!
      ) && simulation.status!.includes('ABORTED')
  );

  const abortedByUser = allSimulations.filter((simulation) => {
    return ['ABORTED_BY_USER'].includes(simulation.status!);
  });

  const abortedByErrorOrTimeout = allSimulations.filter((simulation) => {
    return ['ABORTED_ERROR', 'ABORTED_TIMEOUT'].includes(simulation.status!);
  });

  // Total number of unique human participants
  const uniqueHumanTesters = new Set<string>();

  for (let i = 0; i < (humanReviews || []).length; i++) {
    const humanReview = (humanReviews || [])[i];

    uniqueHumanTesters.add(humanReview.userId || `anonymous_${i}`);
  }

  const humanSucceded =
    humanReviews?.filter((review) => review.status === 'COMPLETED') || [];
  const humanFailed =
    humanReviews?.filter((review) => review.status === 'CANCELED') || [];

  const confidence = useConfidence(
    failed.length,
    succeded.length,
    humanFailed.length,
    humanSucceded.length
  );

  const statistics = useMemo(() => {
    if (!scenario || !simulationsSummary || !humanReviews || !humanEvents)
      return null;

    const total =
      succeded.length +
      failed.length +
      humanSucceded.length +
      humanFailed.length;

    const incorrectSimulationsActions = correctSimulations.reduce(
      (acc, curr) => acc + (curr.incorrectActions || 0),
      0
    );

    const correctSimulationsActions = correctSimulations.reduce(
      (acc, curr) => acc + (curr.correctActions || 0),
      0
    );

    const simsSortedByNumberOfActions = correctSimulations
      .map((sim) => (sim.correctActions || 0) + (sim.incorrectActions || 0))
      .sort((a, b) => a - b);

    const viewsCount = correctSimulations.map((s) => {
      const stepChecksums = s.steps?.map((step) =>
        getViewChecksum(step.pageId || '', step.partials || [], pagesMap)
      );
      const uniqueChecksums = new Set(stepChecksums);

      return uniqueChecksums.size;
    });

    const totalViewsCount = viewsCount.reduce((acc, curr) => acc + curr, 0);
    const simsSortedByViews = viewsCount.sort((a, b) => a - b);

    const clickEvents = humanEvents.filter(
      (event) => event.eventType === 'CLICK'
    );

    const totalUserClicks = clickEvents.length;
    const userClicksWithReaction = clickEvents.filter(
      (clickEvent) => !!clickEvent.isReaction
    ).length;
    const userMissClicks = totalUserClicks - userClicksWithReaction;

    const missClickRatio =
      (incorrectSimulationsActions + userMissClicks) /
      (totalUserClicks +
        (incorrectSimulationsActions + correctSimulationsActions));

    const totalSimulationsDuration = correctSimulations.reduce(
      (acc, curr) => acc + (curr.duration || 0),
      0
    );

    const totalHumanDuration = humanReviews.reduce(
      (acc, curr) =>
        acc +
        (differenceInSeconds(curr.finishTime || new Date(), curr.startTime) ||
          0),
      0
    );

    const totalDuration = totalSimulationsDuration + totalHumanDuration;
    const avgDuration =
      totalDuration / (correctSimulations.length + humanReviews.length);

    const dropOffs = (failed.length + humanFailed.length) / total;

    const viewLoadEvents = humanEvents
      .sort((a, b) => a.eventTimestamp - b.eventTimestamp)
      .filter((event) => event.eventType === 'VIEW_ENTER');

    const viewLoadEventsByUserAndReview: {
      [pageId: string]: {
        [userId: string]: {
          [reviewId: string]: number;
        };
      };
    } = {};

    for (const event of viewLoadEvents) {
      const { userId, reviewId, pageId } = event;
      const fullReview = humanReviews.find((review) => review.id === reviewId);
      if (!reviewId || !pageId || !fullReview) continue;
      const parsedUserId = userId || 'anonymous';

      if (!viewLoadEventsByUserAndReview[pageId]) {
        viewLoadEventsByUserAndReview[pageId] = {};
      }

      if (!viewLoadEventsByUserAndReview[pageId][parsedUserId]) {
        viewLoadEventsByUserAndReview[pageId][parsedUserId] = {};
      }

      viewLoadEventsByUserAndReview[pageId][parsedUserId][reviewId] =
        (viewLoadEventsByUserAndReview[pageId][parsedUserId][reviewId] || 0) +
        1;
    }

    const pageHitsBySimulation: {
      [pageId: string]: {
        [simulationId: string]: number;
      };
    } = {};

    for (const scenarioPage of scenario.prototypePages || []) {
      pageHitsBySimulation[scenarioPage.id!] = {};
    }

    for (let i = 0; i < correctSimulations.length; i++) {
      const simulation = correctSimulations[i];
      const { steps } = simulation;
      if (!steps) continue;

      for (const step of steps) {
        const { pageId } = step;
        if (!pageId) continue;
        const simulationId = `simulation_${i}`;
        pageHitsBySimulation[pageId][simulationId] =
          (pageHitsBySimulation[pageId][simulationId] || 0) + 1;
      }
    }

    const successRatio = (succeded.length + humanSucceded.length) / total;

    const meetsRequirements = areRequirementsMeet(
      humanReviews.length,
      succeded.length + failed.length,
      missClickRatio,
      dropOffs,
      successRatio,
      avgDuration,
      config
    );

    return {
      simulatedReviews: {
        succeded: succeded.length,
        failed: failed.length,
        abortedByErrorOrTimeout: abortedByErrorOrTimeout.length,
        abortedByUser: abortedByUser.length,
      },
      humanReviews: {
        succeded: humanSucceded.length,
        failed: humanFailed.length,
      },
      metrics: {
        humanInteractions: totalUserClicks,
        simulatedInteractions:
          incorrectSimulationsActions + correctSimulationsActions,
        minNumberOfSimInteracitons: simsSortedByNumberOfActions[0],
        maxNumberOfSimInteracitons:
          simsSortedByNumberOfActions[simsSortedByNumberOfActions.length - 1],
        simulationViewsCount: totalViewsCount,
        minNumberOfViews: simsSortedByViews[0],
        maxNumberOfViews: simsSortedByViews[simsSortedByViews.length - 1],
        missClicks: missClickRatio,
        avgDuration,
        dropOffs,
        successRatio,
      },
      confidence,
      simulatedViews: pageHitsBySimulation,
      humanViews: viewLoadEventsByUserAndReview,
      uniqueHumanTesters: uniqueHumanTesters.size,
      meetsRequirements,
    };
  }, [
    pagesMap,
    config,
    scenario,
    simulationsSummary,
    humanReviews,
    humanEvents,
    humanFailed,
    abortedByErrorOrTimeout,
    abortedByUser,
    humanSucceded,
    failed,
    succeded,
    confidence,
    correctSimulations,
    uniqueHumanTesters,
    humanReviews,
  ]);

  return statistics;
};
